/* eslint-disable no-param-reassign */
/* eslint-disable no-restricted-syntax */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import FloatingActionButton from '#component/device/general/floating-action-button';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import {
  MapContainer,
  MapPreloaderContainer,
  MapPreloaderShadow,
  MapPreloader
} from '#component/device/dashboard/styles-map';
import DevicesListWrapper from './devices-list-wrapper';
import ResetButton from './reset-button';
import Locations from './locations';
import { isWidth, getMapCenter } from '#helper';

import './style.css';

const { VITE_MAPBOX_GLJS_API, VITE_MAPBOX_GLJS_STYLE } = import.meta.env;

function updateMap(map, data) {
  map.app.geojson.data = data.data;
  map.app.latestGeojson = data;
  const locations = map.getSource('locations');
  if (locations) locations.setData(map.app.geojson.data);
}

function initMap(data, account, meta, history, theme, helper) {
  const { mapboxgl } = window;
  let { minLng, maxLat, maxLng, minLat, ...geojson } = data;

  mapboxgl.accessToken = VITE_MAPBOX_GLJS_API;

  const map = new mapboxgl.Map({
    container: 'map',
    style: VITE_MAPBOX_GLJS_STYLE,
    bounds: [[minLng, minLat], [maxLng, maxLat]],
    fitBoundsOptions: {
      padding: 50,
      ...(
        !geojson.data.features.length ? { zoom: 1 } : { maxZoom: 10 }
      )
    },
    showZoom: true
  });
  map.app = { account, meta, history, theme, helper, geojson };

  const nav = new mapboxgl.NavigationControl({ showCompass: false });
  map.addControl(nav, 'top-right');

  // disable map rotation using right click + drag
  map.dragRotate.disable();

  // disable map rotation using touch rotation gesture
  map.touchZoomRotate.disableRotation();

  map.on('load', () => {
    map.addSource('locations', Locations.addClusterProperties(geojson, meta));
    Locations.addLayer(map);

    Locations.addPlaceHover();
    Locations.addPlaceClick();
    Locations.addClusterHover();
    Locations.addClusterClick();

    map.addControl(
      new ResetButton(() => {
        if (map.app.latestGeojson) {
          minLng = map.app.latestGeojson.minLng;
          maxLat = map.app.latestGeojson.maxLat;
          maxLng = map.app.latestGeojson.maxLng;
          minLat = map.app.latestGeojson.minLat;
          geojson = map.app.latestGeojson.geojson;
        }
        map.fitBounds([[minLng, maxLat], [maxLng, minLat]], { padding: 50, maxZoom: 10 });
      })
    );
    helper.setMapInit(false);
  });

  return map;
}

const DeviceDashboardMap = props => {
  const {
    loading,
    devices,
    meta,
    history,
    account,
    filter,
    isDevicesLoading
  } = props;
  const theme = useTheme();
  const [clickedDevices, setClickedDevices] = useState([]);
  const [mapInit, setMapInit] = useState(true);
  const map = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const geojson = useMemo(
    () =>
      (!loading ? Locations.getGeoJSON(devices, getMapCenter(account)) : null),
    [devices, loading, account]
  );

  useEffect(
    () => {
      if (meta.device && geojson && (!filter || (filter && devices.length))) {
        if (!map.current) {
          map.current = initMap(
            geojson,
            account,
            meta,
            history,
            theme,
            { setClickedDevices, setMapInit }
          );
        } else {
          updateMap(map.current, geojson);
        }
      }
      return () => {
        // resets to init state after mqtt update
        // map.current = null;
      };
    },
    [geojson, account, meta, history, theme, map, filter, devices.length]
  );

  if (loading) {
    return <PreloaderComponent />;
  }

  if (!loading && filter && !devices.length) {
    map.current = null;
    return (
      <NotFoundMessage>
        Selected devices do not have locations
      </NotFoundMessage>
    );
  }

  return (
    <MapContainer className={isWidth(1200) ? 'mob' : ''}>
      {!loading && clickedDevices.length ?
        <DevicesListWrapper
          account={account}
          devices={clickedDevices}
          closeList={() => setClickedDevices([])}
        /> :
        null }
      <div id="map" className="devicesDashboardMap" style={{ height: '100%' }} />
      {isMobile &&
        <FloatingActionButton
          onClickFabButton={() => history.push('/device?order=asc&orderBy=title&occupied=all')}
          buttonIcon={<ListIcon />}
          buttonName="List"
          buttonColor="inherit"
          textColor={theme.palette.secondary.main}
        />}
      {(mapInit || isDevicesLoading) &&
        <MapPreloaderContainer>
          <MapPreloaderShadow />
          <MapPreloader />
        </MapPreloaderContainer>}
    </MapContainer>
  );
};

export default DeviceDashboardMap;
